// components/editor/anteditor/antRichContent.js
export function createAntRichContent(root, options = {}) {
  const doc = root.ownerDocument, win = doc.defaultView;
  const mobile = !!options.mobile;
  root.classList.add('ant-rich-content');
  const host = doc.createElement('div');
  host.className = 'ant-rich-ui' + (mobile ? ' ant-rich-mobile' : '');
  host.setAttribute('data-ant-ui', 'true');
  doc.body.append(host);
  let savedRange = null, linkRange = null, selectedLink = null, destroyed = false, frame = 0;
  let requestId = 0;
  const pending = new Map(), bars = new Map();
  const element = (node) => node?.nodeType === 3 ? node.parentElement : node;
  const inside = (node, selector) => {
    const found = element(node)?.closest?.(selector);
    return found && root.contains(found) ? found : null;
  };
  const editable = () => root.isContentEditable;
  const validRange = (range) => range && root.contains(range.commonAncestorContainer);
  const listen = [];
  function on(node, event, fn, capture = false) {
    node.addEventListener(event, fn, capture);
    listen.push(() => node.removeEventListener(event, fn, capture));
  }
  function safeURL(value, webOnly = false) {
    const text = String(value || '').trim();
    if (!text || text.includes('\\') || /[\u0000-\u0020\u007f]/.test(text)) return '';
    // Relative app links are supported; the native parent can handle them via onLink.
    if (!webOnly && /^(\/[^/]|\/$|#)/.test(text)) return text;
    const candidate = /^www\./i.test(text) ? 'https://' + text : text;
    try {
      const url = new win.URL(candidate);
      if (url.username || url.password) return '';
      if (!['http:', 'https:', ...(webOnly ? [] : ['mailto:', 'tel:'])].includes(url.protocol)) return '';
      return url.href;
    } catch (_) { return ''; }
  }
  function remember() {
    const sel = win.getSelection();
    if (sel.rangeCount && validRange(sel.getRangeAt(0))) savedRange = sel.getRangeAt(0).cloneRange();
  }
  function restore(range = savedRange) {
    root.focus({ preventScroll: true });
    if (!validRange(range)) {
      range = doc.createRange(); range.selectNodeContents(root); range.collapse(false);
    }
    const sel = win.getSelection(); sel.removeAllRanges(); sel.addRange(range);
    savedRange = range.cloneRange();
    return range;
  }
  function changed() { options.onChange?.(); schedule(); }
  function clean(container) {
    container.querySelectorAll('[data-ant-ui], .anteditor-code-copy-btn, .ant-code-copy-btn').forEach((node) => node.remove());
    // Known fragments from the old saved toolbar only; never remove literal code text.
    container.querySelectorAll('pre > .copy-icon, pre > .copy-text').forEach((node) => node.remove());
    container.querySelectorAll('[data-ant-insert]').forEach((node) => node.removeAttribute('data-ant-insert'));
    container.querySelectorAll('a[href]').forEach((a) => {
      const href = safeURL(a.getAttribute('href'));
      if (href) { a.setAttribute('href', href); a.setAttribute('rel', 'noopener noreferrer'); }
      else a.removeAttribute('href');
    });
  }
  function html() { const clone = root.cloneNode(true); clean(clone); return clone.innerHTML; }
  function label(url) { try { return new win.URL(url).hostname.replace(/^www\./, ''); } catch (_) { return url; } }
  function makeCard(data) {
    const card = doc.createElement('div');
    card.className = 'ant-link-card'; card.contentEditable = 'false';
    card.dataset.antKind = data.mode; card.dataset.antUrl = data.url;
    const a = doc.createElement('a'); a.href = data.url; a.rel = 'noopener noreferrer';
    if (safeURL(data.imageUrl, true)) {
      const img = doc.createElement('img'); img.className = 'ant-link-image';
      img.src = safeURL(data.imageUrl, true); img.alt = ''; a.append(img);
    }
    const body = doc.createElement('span'); body.className = 'ant-link-body';
    const mark = doc.createElement('span'); mark.className = 'ant-link-mark'; mark.textContent = label(data.url).slice(0, 1).toUpperCase();
    const title = doc.createElement('span'); title.className = 'ant-link-title'; title.textContent = data.caption || label(data.url);
    const address = doc.createElement('span'); address.className = 'ant-link-address'; address.textContent = data.url;
    body.append(mark, title, address); a.append(body); card.append(a);
    return card;
  }
  function prepareLink(mode = 'link', target = null) {
    remember(); linkRange = validRange(savedRange) ? savedRange.cloneRange() : null;
    selectedLink = target || inside(linkRange?.startContainer, '.ant-link-card, a[href]');
    selectedLink = selectedLink?.closest('.ant-link-card') || selectedLink;
    const card = selectedLink?.matches('.ant-link-card');
    const payload = {
      mode: card ? selectedLink.dataset.antKind : mode,
      url: selectedLink?.getAttribute(card ? 'data-ant-url' : 'href') || '',
      caption: card ? selectedLink.querySelector('.ant-link-title')?.textContent || '' : selectedLink?.textContent || linkRange?.toString() || '',
      imageUrl: card ? selectedLink.querySelector('.ant-link-image')?.getAttribute('src') || '' : '',
    };
    hidePopover(); options.onRequestLink?.(payload); return payload;
  }
  function insertLink(data) {
    if (!editable()) return 'This document is read-only.';
    const mode = ['link', 'card', 'embed'].includes(data.mode) ? data.mode : 'link';
    const url = safeURL(data.url, mode !== 'link');
    if (!url) return mode === 'link' ? 'Enter a valid https://, http://, mailto:, tel: or app-relative link.' : 'Cards and embeds need an http:// or https:// URL.';
    if (mode !== 'link' && data.imageUrl && !safeURL(data.imageUrl, true)) return 'Enter a valid http:// or https:// image URL, or leave it empty.';
    const range = restore(linkRange || savedRange);
    const node = mode === 'link' ? doc.createElement('a') : makeCard({ ...data, url, mode });
    if (mode === 'link') {
      node.href = url; node.rel = 'noopener noreferrer';
      if (!selectedLink && !range.collapsed && data.caption === range.toString()) {
        const contents = range.cloneContents(); contents.querySelectorAll('a').forEach((a) => a.replaceWith(...a.childNodes));
        node.append(contents);
      } else if (selectedLink?.matches('a') && data.caption === selectedLink.textContent) {
        node.append(...[...selectedLink.childNodes].map((child) => child.cloneNode(true)));
      } else node.textContent = data.caption || url;
    }
    if (selectedLink?.matches('.ant-link-card') && root.contains(selectedLink)) {
      // Browsers can refuse insertHTML over a non-editable card containing images.
      // Replace this one atomic block; an inline link receives a paragraph wrapper.
      const replacement = mode === 'link' ? doc.createElement('p') : node;
      if (mode === 'link') replacement.append(node);
      selectedLink.replaceWith(replacement); setAfter(node, mode !== 'link');
      selectedLink = null; linkRange = null; changed(); return '';
    }
    if (selectedLink && root.contains(selectedLink)) {
      range.selectNode(selectedLink); restore(range);
    }
    // Native insertion splits paragraphs around cards and replaces selected text
    // in one transaction. Never persist an invalid div-inside-p structure.
    const marker = 'ant-insert-' + (++requestId);
    node.setAttribute('data-ant-insert', marker);
    if (!doc.execCommand('insertHTML', false, node.outerHTML)) return 'The link could not be inserted. Place the cursor in the note and try again.';
    const inserted = root.querySelector('[data-ant-insert="' + marker + '"]');
    if (inserted) { inserted.removeAttribute('data-ant-insert'); setAfter(inserted, mode !== 'link'); }
    selectedLink = null; linkRange = null; changed(); return '';
  }

  function setAfter(node, block) {
    const range = doc.createRange();
    if (block) {
      let p = node.nextElementSibling;
      if (!p || !p.matches('p,div') || p.contentEditable === 'false') {
        p = doc.createElement('p'); p.append(doc.createElement('br')); node.after(p);
      }
      range.selectNodeContents(p); range.collapse(true);
    } else { range.setStartAfter(node); range.collapse(true); }
    restore(range);
  }
  function insertCode() {
    if (!editable()) return;
    remember(); const range = restore();
    if (inside(range.startContainer, 'pre')) return;
    const pre = doc.createElement('pre'), code = doc.createElement('code');
    code.textContent = range.toString(); if (!code.textContent) code.append(doc.createElement('br'));
    pre.append(code); const marker = 'ant-code-' + (++requestId); pre.dataset.antInsert = marker;
    doc.execCommand('insertHTML', false, pre.outerHTML + '<p><br></p>');
    const inserted = root.querySelector('[data-ant-insert="' + marker + '"]');
    if (inserted) { inserted.removeAttribute('data-ant-insert'); const next = doc.createRange(); next.selectNodeContents(inserted.firstChild); next.collapse(false); restore(next); }
    changed();
  }
  const iconPaths = {
    Copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
    'Continue below': '<path d="M6 3v10a4 4 0 0 0 4 4h10m-4-4 4 4-4 4"/>',
    Paste: '<rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3"/>',
  };
  function button(text, action, icons = false) {
    const btn = doc.createElement('button'); btn.type = 'button'; btn.title = text; btn.setAttribute('aria-label', text);
    if (icons) btn.innerHTML = '<svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + iconPaths[text] + '</svg>';
    if (!icons || !mobile) { const span = doc.createElement('span'); span.textContent = text; btn.append(span); }
    btn.addEventListener('pointerdown', (event) => { remember(); event.preventDefault(); });
    btn.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); action(); });
    return btn;
  }
  const status = doc.createElement('div'); status.className = 'ant-rich-status'; status.setAttribute('role', 'status'); host.append(status);
  let statusTimer;
  function announce(message) { status.textContent = message; clearTimeout(statusTimer); statusTimer = setTimeout(() => { status.textContent = ''; }, 3000); }
  function clipboard(action, target, text) {
    remember();
    const id = ++requestId;
    pending.set(id, { action, target, range: validRange(savedRange) ? savedRange.cloneRange() : null });
    if (options.onClipboard) { options.onClipboard({ id, action, text: text || '' }); return; }
    const api = win.navigator.clipboard;
    const task = action === 'copy' ? api?.writeText(text || '') : api?.readText();
    if (!task) { clipboardResult({ id, error: 'Clipboard unavailable. Use your device’s copy/paste menu.' }); return; }
    task.then((value) => clipboardResult({ id, text: value || '' })).catch(() => clipboardResult({ id, error: 'Clipboard access was declined. Use your device’s copy/paste menu.' }));
  }
  function clipboardResult(result) {
    const job = pending.get(result.id); pending.delete(result.id);
    if (!job || destroyed) return;
    if (result.error) { announce(result.error); return; }
    if (job.action === 'copy') { announce('Copied'); return; }
    if (!editable() || !root.contains(job.target)) return;
    let range = job.range;
    if (!validRange(range) || !job.target.contains(range.commonAncestorContainer)) {
      range = doc.createRange(); range.selectNodeContents(job.target.querySelector('code') || job.target); range.collapse(false);
    }
    restore(range); doc.execCommand('insertText', false, String(result.text || '')); changed(); announce('Pasted');
  }
  let popover = null, activeLink = null;
  function hidePopover() { popover?.remove(); popover = null; activeLink = null; }
  function openURL(url) {
    const safe = safeURL(url); if (!safe) return;
    if (options.onOpenLink) options.onOpenLink(safe);
    else win.open(safe, '_blank', 'noopener,noreferrer');
  }
  function showPopover(target) {
    hidePopover(); activeLink = target;
    const card = target.closest('.ant-link-card');
    const url = card?.dataset.antUrl || target.getAttribute('href');
    popover = doc.createElement('div'); popover.className = 'ant-link-popover';
    const address = doc.createElement('div'); address.className = 'ant-popover-address'; address.textContent = url;
    const actions = doc.createElement('div'); actions.className = 'ant-link-actions';
    actions.append(button('Open', () => { openURL(url); hidePopover(); }), button('Copy link', () => clipboard('copy', null, url)));
    if (editable()) actions.append(button('Edit', () => prepareLink('link', card || target)), button(card ? 'Remove' : 'Unlink', () => {
      if (card) card.remove(); else target.replaceWith(...target.childNodes);
      hidePopover(); changed();
    }));
    popover.append(address, actions);
    if (card?.dataset.antKind === 'embed' && !mobile) popover.append(button('Load embed', () => { loadEmbed(card); hidePopover(); }));
    host.append(popover); layout();
  }
  function embedURL(url) {
    const u = new win.URL(url);
    if (/^(www\.)?(youtube\.com|youtu\.be)$/.test(u.hostname)) {
      const id = u.hostname === 'youtu.be' ? u.pathname.slice(1) : u.searchParams.get('v') || u.pathname.split('/').pop();
      if (/^[\w-]{11}$/.test(id)) return 'https://www.youtube-nocookie.com/embed/' + id;
    }
    if (/^(www\.)?vimeo\.com$/.test(u.hostname) && /^\/\d+$/.test(u.pathname)) return 'https://player.vimeo.com/video' + u.pathname;
    return u.href;
  }
  function loadEmbed(card) {
    if (card.querySelector('iframe')) return;
    const source = safeURL(card.dataset.antUrl, true); if (!source) return;
    const frame = doc.createElement('iframe'); frame.dataset.antUi = 'true'; frame.src = embedURL(source);
    frame.title = card.querySelector('.ant-link-title')?.textContent || 'Embedded page';
    const sameOrigin = new win.URL(source).origin === win.location.origin;
    frame.setAttribute('sandbox', sameOrigin ? 'allow-scripts allow-presentation' : 'allow-scripts allow-same-origin allow-presentation');
    frame.referrerPolicy = 'no-referrer'; frame.loading = 'lazy'; frame.allowFullscreen = true;
    card.append(frame); schedule();
  }
  function decorate() {
    // One-way migration of known UI clutter and old frames; no blanket text deletion.
    root.querySelectorAll('.anteditor-code-copy-btn, .ant-code-copy-btn, pre > .copy-icon, pre > .copy-text').forEach((el) => el.remove());
    root.querySelectorAll('.ant-video-card').forEach((card) => {
      const a = card.querySelector('a[href]'), url = safeURL(a?.getAttribute('href'), true);
      if (url) card.replaceWith(makeCard({ mode: 'embed', url, caption: label(url), imageUrl: card.querySelector('img')?.getAttribute('src') || '' }));
    });
    root.querySelectorAll('iframe:not([data-ant-ui])').forEach((frame) => {
      const url = safeURL(frame.getAttribute('src'), true);
      if (url) frame.replaceWith(makeCard({ mode: 'embed', url, caption: frame.title || label(url) }));
    });
    root.querySelectorAll('.ant-link-card').forEach((card) => {
      if (card.contentEditable !== 'false') card.contentEditable = 'false';
      const url = safeURL(card.dataset.antUrl, true);
      // Favicon is runtime decoration; it never becomes saved content.
      const mark = card.querySelector('.ant-link-mark');
      if (url && mark && !mark.querySelector('img')) {
        const img = doc.createElement('img'); img.dataset.antUi = 'true'; img.alt = ''; img.referrerPolicy = 'no-referrer'; img.loading = 'lazy';
        img.src = new win.URL('/favicon.ico', url).href;
        img.onerror = () => { img.style.display = 'none'; };
        mark.append(img);
      }
    });
    root.querySelectorAll('pre,blockquote').forEach((pre) => {
      if (bars.has(pre)) return;
      const bar = doc.createElement('div'); bar.className = 'ant-code-actions';
      if (pre.matches('pre')) {
        const copy = button('Copy', () => clipboard('copy', pre, pre.innerText || pre.textContent || ''), true);
        const paste = button('Paste', () => clipboard('paste', pre), true);
        paste.dataset.edit = 'true'; bar.append(copy, paste);
      }
      const exit = button('Continue below', () => options.onContinueBelow?.(pre), true);
      exit.dataset.edit = 'true'; bar.append(exit);
      bar.addEventListener('pointerenter', () => bar.classList.add('is-hovered'));
      bar.addEventListener('pointerleave', (event) => { if (!pre.contains(event.relatedTarget)) bar.classList.remove('is-hovered'); });
      host.append(bar); bars.set(pre, bar);
      bar.classList.toggle('is-hovered', pre.matches(':hover'));

    });
    bars.forEach((bar, pre) => { if (!root.contains(pre)) { bar.remove(); bars.delete(pre); } });
  }
  function layout() {
    const style = win.getComputedStyle(root);
    let painted = root;
    while (painted.parentElement && ['transparent', 'rgba(0, 0, 0, 0)'].includes(win.getComputedStyle(painted).backgroundColor)) painted = painted.parentElement;
    host.style.setProperty('--ant-rich-paper', win.getComputedStyle(painted).backgroundColor === 'rgba(0, 0, 0, 0)' ? 'Canvas' : win.getComputedStyle(painted).backgroundColor);
    host.style.color = style.color;
    const rootRect = root.getBoundingClientRect();
    bars.forEach((bar, pre) => {
      const rect = pre.getBoundingClientRect();
      const visible = rect.width && rect.top >= Math.max(0, rootRect.top) - 4 && rect.top < Math.min(win.innerHeight, rootRect.bottom) - 25;
      bar.style.display = visible ? 'flex' : 'none';
      bar.style.top = (rect.top + 5) + 'px'; bar.style.left = Math.max(rootRect.left, rect.right - bar.offsetWidth - 6) + 'px';
      bar.querySelectorAll('[data-edit]').forEach((button) => { button.hidden = !editable(); });
      if (!editable() && pre.matches('blockquote')) bar.style.display = 'none';
    });
    if (popover && activeLink?.isConnected) {
      const rect = activeLink.getBoundingClientRect(), w = Math.min(310, win.innerWidth - 16);
      popover.style.width = w + 'px'; popover.style.left = Math.max(8, Math.min(rect.left, win.innerWidth - w - 8)) + 'px';
      popover.style.top = Math.max(8, Math.min(rect.bottom + 5, win.innerHeight - popover.offsetHeight - 8)) + 'px';
    }
  }
  function schedule() {
    if (!frame && !destroyed) frame = win.requestAnimationFrame(() => { frame = 0; decorate(); layout(); options.onLayout?.(); });
  }
  on(doc, 'selectionchange', remember);
  on(root, 'pointerover', (event) => { bars.get(inside(event.target, 'pre,blockquote'))?.classList.add('is-hovered'); });
  on(root, 'pointerout', (event) => {
    const pre = inside(event.target, 'pre,blockquote'), bar = bars.get(pre);
    if (bar && !pre.contains(event.relatedTarget) && !bar.contains(event.relatedTarget)) bar.classList.remove('is-hovered');
  });
  on(root, 'click', (event) => {
    const a = inside(event.target, 'a[href]'); if (!a) return;
    event.preventDefault(); event.stopPropagation();
    if (!mobile && (event.metaKey || event.ctrlKey)) openURL(a.getAttribute('href'));
    else { remember(); showPopover(a); }
  }, true);
  on(doc, 'pointerdown', (event) => { if (!host.contains(event.target) && !inside(event.target, 'a')) hidePopover(); }, true);
  on(doc, 'keydown', (event) => { if (event.key === 'Escape') hidePopover(); });
  on(root, 'paste', (event) => {
    if (!editable()) return;
    remember(); const range = restore(), clip = event.clipboardData;
    if (!clip) return;
    const text = clip.getData('text/plain');
    if (inside(range.startContainer, 'pre,code')) {
      event.preventDefault(); event.stopImmediatePropagation(); doc.execCommand('insertText', false, text); changed(); return;
    }
    const url = safeURL(text);
    if (url && !/[\r\n]/.test(text.trim())) {
      event.preventDefault(); event.stopImmediatePropagation();
      linkRange = range.cloneRange(); selectedLink = null;
      insertLink({ url, caption: range.toString() || text.trim(), mode: 'link' }); return;
    }
    if (options.sanitize && clip.getData('text/html')) {
      event.preventDefault(); event.stopImmediatePropagation(); doc.execCommand('insertHTML', false, options.sanitize(clip.getData('text/html'))); changed();
    }
  }, true);
  on(root, 'load', schedule, true);
  on(root, 'input', () => { remember(); schedule(); });
  on(doc, 'scroll', layout, true); on(win, 'resize', layout);
  const observer = new win.MutationObserver(schedule); observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['contenteditable'] });
  const resize = win.ResizeObserver ? new win.ResizeObserver(schedule) : null; resize?.observe(root);
  decorate(); layout();
  return { html, decorate: schedule, remember, prepareLink, insertLink, insertCode, clipboardResult,
    destroy() { destroyed = true; listen.forEach((off) => off()); observer.disconnect(); resize?.disconnect(); win.cancelAnimationFrame(frame); clearTimeout(statusTimer); pending.clear(); host.remove(); }
  };
}
